import { faBuildingColumns } from '@fortawesome/pro-light-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Badge } from '@shared/components/ui/badge';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@shared/components/ui/shadcn/table';
import { useTranslation } from 'react-i18next';
import { useClientBankAccounts } from '../../hooks';

interface BankAccountsTabProps {
  teamId: number;
}

export const BankAccountsTab = ({ teamId }: BankAccountsTabProps) => {
  const { t } = useTranslation('viewClientModule');
  const { bankAccounts, total, isLoading } = useClientBankAccounts(teamId);

  const getStatusVariant = (status: string) => {
    switch (status?.toUpperCase()) {
      case 'VERIFIED':
        return 'default';
      case 'PENDING':
        return 'secondary';
      case 'REJECTED':
        return 'destructive';
      case 'DISABLED':
        return 'outline';
      default:
        return 'outline';
    }
  };

  const getStatusLabel = (status: string) => {
    switch (status?.toUpperCase()) {
      case 'VERIFIED':
        return t('tabs.bankAccounts.statusVerified');
      case 'PENDING':
        return t('tabs.bankAccounts.statusPending');
      case 'REJECTED':
        return t('tabs.bankAccounts.statusRejected');
      case 'DISABLED':
        return t('tabs.bankAccounts.statusDisabled');
      default:
        return status || '-';
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  if (bankAccounts.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center py-12 text-muted-foreground">
        <FontAwesomeIcon icon={faBuildingColumns} className="w-12 h-12 mb-4 opacity-50" />
        <p>{t('tabs.bankAccounts.empty')}</p>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <p className="text-sm text-muted-foreground">{t('tabs.bankAccounts.total', { count: total })}</p>
      </div>
      <div className="border border-card-border rounded-lg">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>{t('tabs.bankAccounts.bankName')}</TableHead>
              <TableHead>{t('tabs.bankAccounts.holder')}</TableHead>
              <TableHead>{t('tabs.bankAccounts.iban')}</TableHead>
              <TableHead>{t('tabs.bankAccounts.bic')}</TableHead>
              <TableHead>{t('tabs.bankAccounts.country')}</TableHead>
              <TableHead className="text-center">{t('tabs.bankAccounts.status')}</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {bankAccounts.map((account) => (
              <TableRow key={account.id}>
                <TableCell className="font-medium">{account.bank_name || t('tabs.bankAccounts.unknown')}</TableCell>
                <TableCell>{account.holder_name || '-'}</TableCell>
                <TableCell className="font-mono text-xs">{account.iban || '-'}</TableCell>
                <TableCell className="font-mono text-xs">{account.bic || '-'}</TableCell>
                <TableCell>{account.country_code || '-'}</TableCell>
                <TableCell className="text-center">
                  <Badge variant={getStatusVariant(account.status)}>{getStatusLabel(account.status)}</Badge>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>
    </div>
  );
};
